import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../auth.service';
import {MessageService} from '../../../messages/message.service';
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {PasswordPromptComponent} from "./password-prompt/password-prompt.component";
import {TranslateService} from "../../../shared/translate.service";
import {HtmlUtilsService} from "../../../html-utils/html-utils.service";
import {Router} from '@angular/router';
import {AppConfigService} from "../../../config/app-config.service";
import {Subscription} from "rxjs";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit, OnDestroy {

  form!: FormGroup;

  password = '';

  newPassword = '';

  confirmPassword = '';

  oldEmail = '';

  email = '';

  passwordInit: Promise<boolean> = Promise.resolve(false);

  termsAccepted: boolean;

  passwordPolicyVisible: boolean;

  passwordLabel: string = 'Account.newPassword.label';

  username: string = '';

  submitted = false;

  private isLoggedSubscription: Subscription;
  private timeout: number;

  constructor(private authService: AuthService,
              private messageService: MessageService,
              private modalService: NgbModal,
              private translateService: TranslateService,
              private htmlUtils: HtmlUtilsService,
              private router: Router,
              private appConfig: AppConfigService,
              private formBuilder: FormBuilder,) {

  }

  ngOnInit() {
    this.form = this.formBuilder.group({
        currentPassword: new FormControl(
          {value: '', disabled: !this.passwordInit},
          [
            Validators.required,
            Validators.pattern(
              '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[-+_!@#$%^&*.,?]).{10,}$'
            ),
          ],
        ),
        newPassword: new FormControl(
          '',
          [
            Validators.required,
            Validators.pattern(
              '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[-+_!@#$%^&*.,?]).{10,}$'
            ),
          ],
        ),
        confirmPassword: new FormControl(
          '',
          [
            Validators.required,
            Validators.pattern(
              '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[-+_!@#$%^&*.,?]).{10,}$'
            ),
          ],
        )
      },
      {
        validator: [
          this.comparePassword("newPassword", "confirmPassword"),
          this.validateCurrentPassword("currentPassword")
        ]
      });

    this.timeout = this.appConfig.accountConfig.timeout;
    this.passwordPolicyVisible = false;

    let currentUser = this.authService.currentUserValue;
    if (currentUser && currentUser.email) {
      this.oldEmail = currentUser.email;
      this.username = currentUser.email;
    }

    this.isLoggedSubscription = this.authService.isLogged.subscribe(loggedIn => {
      if (!loggedIn) {
        this.router.navigate(['/']).then(() => {
          this.messageService.successMessage('Account.logout', 'Account.logout.info');
        });
      } else {
        this.checkPasswordStatus();
      }
    });

    this.showPasswordPolicy();

    this.termsAccepted = true;
  }

  comparePassword(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        return;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({mustMatch: true});
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  validateCurrentPassword(controlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      if (!this.authService.validatePassword(control.value)) {
        control.setErrors({mustMatch: true});
      }
    }
  }

  checkPasswordStatus() {
    this.authService.getToken().then(token => {
      this.authService.checkPasswordStatus(token).subscribe(value => {
        this.changeLabel(value);
        this.passwordInit = Promise.resolve(value);
      }, error => {
        console.warn("Cannot check credentials status: ", error);
        this.changeLabel(false);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.isLoggedSubscription) {
      this.isLoggedSubscription.unsubscribe();
    }
  }

  private changeLabel(value: boolean) {
    if (value) {
      this.passwordLabel = 'Account.newPassword.label';
    } else {
      this.passwordLabel = 'Account.noPassword.label';
    }
  }


  onPassSubmit() {
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }

    this.authService.getToken().then(token => {
      this.authService.changePassword(this.formField.newPassword.value.trim(), token).subscribe(value => {
        this.messageService.successMessage(
          'Account.change-password.success.title',
          'Account.change-password.success.message'
        );
        setTimeout(() => {
          this.authService.logout();
        }, this.timeout);
      }, error => {
        this.messageService.errorMessage(
          'Account.change-password.error.title',
          'Account.change-password.error.message'
        );
        console.error("Unable to change password: " + error.error);
      }, () => {
        this.checkPasswordStatus();
      })
    }).catch(error => {
      console.error(error);
    });
  }

  declineTerms() {
    this.termsAccepted = false;
    const modalInstance = this.modalService.open(PasswordPromptComponent);
    modalInstance.result.then(result => {
      if (result) {
        this.termsAccepted = false;
        this.messageService.successMessage('Account.delete.success.title', 'Account.delete.success.message');
        setTimeout(() => {
          this.authService.logout();
        }, this.timeout);
      } else {
        this.termsAccepted = true;
      }
    });
    modalInstance.componentInstance.setLabels(this.getDeclineLabels());
  }

  deleteAccount() {
    const modalInstance = this.modalService.open(PasswordPromptComponent);
    modalInstance.result.then(result => {
      if (result) {
        setTimeout(() => {

        }, this.timeout);
        this.authService.logout();
      }
    });
    modalInstance.componentInstance.setLabels(this.getDeleteAccountLabels());
  }

  toggleVisible(id): void {
    this.htmlUtils.toggleVisible(id);
  }

  toggleVisibleDiv(id): void {
    this.htmlUtils.toggleVisibleDiv(id);
  }

  showPasswordPolicy() {
    this.passwordPolicyVisible = !this.passwordPolicyVisible;
  }

  private getDeclineLabels() {
    return {
      modalTitle: this.translateService.translate('Account.terms.warning.title'),
      modalQuestion: this.translateService.translate('Account.terms.warning.message'),
    };
  }

  private getDeleteAccountLabels() {
    return {
      modalTitle: this.translateService.translate('Account.delete.warning.title'),
      modalQuestion: this.translateService.translate('Account.delete.warning.message'),
    };
  }

  get formField() {
    return this.form.controls;
  }
}
